import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function PitaRequest() {
  const [form, setForm] = useState({ name: "", email: "", municipality: "", role: "resident", message: "" });
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.length >= 2 && /.+@.+\..+/.test(form.email) && form.municipality.length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/pita/requests", { ...form, message: form.message || null, website: hp });
      setDone(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="wyv-container py-16">
        <Card className="max-w-xl mx-auto"><CardContent className="p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="font-heading font-bold text-xl" data-testid="pita-request-success">Request received</h1>
          <p className="text-sm text-muted-foreground mt-3">{done}</p>
        </CardContent></Card>
      </div>
    );

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="pita-request-title">
          Request PITA for your community
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Tell us who you are and where you'd like PITA. We'll follow up with what's involved — no obligation.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 max-w-xl">
        <Card><CardContent className="p-6 sm:p-8 space-y-5">
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pita-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="pita-email" />
            </div>
          </div>
          <div>
            <Label>Municipality *</Label>
            <Input className="mt-1.5" placeholder="e.g. City of Kawartha Lakes" value={form.municipality} onChange={(e) => set("municipality", e.target.value)} data-testid="pita-municipality" />
          </div>
          <div>
            <Label className="mb-2 block">I am a… *</Label>
            <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="space-y-1.5">
              {[
                ["resident", "Resident"],
                ["candidate", "Candidate"],
                ["municipality", "Municipality / staff"],
                ["taxpayer_group", "Taxpayer group"],
                ["other", "Other"],
              ].map(([v, l]) => (
                <div key={v} className="flex items-center gap-2">
                  <RadioGroupItem value={v} id={`role-${v}`} data-testid={`pita-role-${v}`} />
                  <Label htmlFor={`role-${v}`} className="font-normal">{l}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label>Anything we should know? (optional)</Label>
            <Textarea className="mt-1.5" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="pita-message" />
          </div>
          <Button type="submit" className="w-full h-11" disabled={!valid || busy} data-testid="pita-submit">
            {busy ? "Submitting…" : "Submit request"}
          </Button>
        </CardContent></Card>
      </form>
    </div>
  );
}
